
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  ICountryConfig,
  IDeclaredValueFingerprint,
  IDeclaredValuePackFingerprint,
  IPickUpService,
  IRequestedAmount,
  ISolCustomerCommodity,
  ISolCustomerPack,
  IUserDetailData,
} from "@/entities";
import DetailConsignment from "@/components/orders/DetailConsignment.vue";
import AgilePickupDeclaredValueTable from "@/components/orders/agile_pickup/AgilePickupDeclaredValueTable.vue";
import AgilePickupDeclaredValuePackTable from "@/components/orders/agile_pickup/AgilePickupDeclaredValuePackTable.vue";
import { Getter } from "vuex-class";
import QrModal from "@/components/orders/agile_pickup/QRModal.vue";
import IconQr from "@/components/common/icon/IconQr.vue";

@Component({
  name: "detail-pickup",
  components: {
    IconQr,
    QrModal,
    AgilePickupDeclaredValueTable,
    AgilePickupDeclaredValuePackTable,
    DetailConsignment,
  },
})
export default class DetailPickup extends Vue {
  @Prop({ required: true })
  pickUpService!: IPickUpService | undefined;
  @Prop({ required: true })
  serviceCode!: string;
  @Prop({ required: true })
  serviceStatusCode!: string;
  @Prop({ required: false, default: false })
  agilePickup!: boolean;
  @Prop({ required: true })
  packs!: ISolCustomerPack[];

  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;

  public loading = false;
  public declaredValues: IDeclaredValueFingerprint | null = null;
  public declaredValuesString: string | null = null;
  public declaredValuesPack: IDeclaredValuePackFingerprint | null = null;
  public declaredValuesPackString: string | null = null;
  private PACK_CODE = "28";

  get isAgilePickupAvailable(): boolean {
    const hasRequestAmounts = !!this.pickUpService?.requestedAmounts?.length || false;
    const hasDeclaredValues =
      !!this.declaredValues?.dt?.dr?.length || !!this.declaredValuesPack?.dt?.dr?.length || false;
    return !hasRequestAmounts || hasDeclaredValues;
  }

  sumRequestedAmounts(
    values: IRequestedAmount[] | undefined
  ): { amount: number; currencyIsoCode: string; currencySymbol: string }[] {
    const amountsByCurrency: { amount: number; currencyIsoCode: string; currencySymbol: string }[] =
      [];

    values?.forEach((value) => {
      const amountByCurrency = amountsByCurrency.find(
        (amountByCurrency) => amountByCurrency.currencyIsoCode === value.currencyIsoCode
      );

      if (amountByCurrency) {
        amountByCurrency.amount = (amountByCurrency.amount ?? 0) + (value.totalAmount ?? 0);
      } else {
        amountsByCurrency.push({
          amount: value.totalAmount ?? 0,
          currencyIsoCode: value.currencyIsoCode ?? "",
          currencySymbol: value.currencySymbol ?? "",
        });
      }
    });
    return amountsByCurrency;
  }

  get commodities(): ISolCustomerCommodity[] {
    return this.pickUpService?.agreementCommodities?.filter((c) => c.code !== this.PACK_CODE) || [];
  }

  get hasPack(): boolean {
    return !!this.pickUpService?.agreementCommodities?.find((c) => c.code === this.PACK_CODE);
  }

  get qrValues(): string[] {
    const values: string[] = [];
    if (this.declaredValues?.fp) {
      values.push(this.declaredValuesString!);
    }
    if (this.declaredValuesPack?.fp) {
      values.push(this.declaredValuesPackString!);
    }
    return values;
  }

  async mounted(): Promise<void> {
    if (this.agilePickup) {
      await this.getDeclaredValues();
    }
  }

  async onRefreshAgilePickup(): Promise<void> {
    this.$emit("refresh");
    await this.getDeclaredValues();
  }

  async getDeclaredValues(): Promise<void> {
    const params = {
      countryCode: this.getLoggedUser.country!.code!,
      serviceCode: this.serviceCode,
    };

    this.loading = true;

    const res = await this.$services.solServiceApi
      .getDeclaredValues(params)
      .finally(() => (this.loading = false));

    this.declaredValues = res?.declaredValues || null;
    this.declaredValuesString = res ? JSON.stringify(res.declaredValues) : null;
    this.declaredValuesPack = res?.declaredValuesPack || null;
    this.declaredValuesPackString = res ? JSON.stringify(res.declaredValuesPack) : null;
  }
}
